import { z } from 'zod';
import { VisitorMode } from '../../types/form-data.types';
import { VisitStepFieldsConfig } from '../../hooks/use-ui-metadata-field-config.hook';

/**
 * Create visitor schema with conditional validations based on ui_metadata config
 */
const createVisitorSchema = (config: VisitStepFieldsConfig) =>
  z.object({
    firstName: z.string().min(1, 'errors.firstNameRequired'),
    lastName: z.string().min(1, 'errors.lastNameRequired'),
    email: config.email.required
      ? z.string().min(1, 'errors.emailRequired').email('errors.pleaseEnterAValidEmail')
      : z.string().email('errors.pleaseEnterAValidEmail').optional().or(z.literal('')),
    phone: config.phone.required ? z.string().min(1, 'errors.phoneRequired') : z.string().optional().or(z.literal('')),
    companyName: config.companyName.required
      ? z.string().min(1, 'errors.companyNameRequired')
      : z.string().optional().or(z.literal('')),
    photoUuid: config.visitorPhoto.required
      ? z.string().min(1, 'errors.visitorPhotoRequired')
      : z.string().optional().or(z.literal('')),
  });

/**
 * Schema for visit step validation with ui_metadata configuration support
 * @param t - Translation function for error messages
 * @param config - Field configuration from ui_metadata
 * @returns Zod schema object
 */
export const createVisitStepSchema = (config: VisitStepFieldsConfig) => {
  const visitorSchema = createVisitorSchema(config);

  return z
    .object({
      visitorMode: z.enum([VisitorMode.SINGLE, VisitorMode.MULTIPLE]),
      visitSummary: config.visitSummary.required
        ? z.string().min(1, 'errors.visitSummaryRequired').max(100, 'errors.visitSummaryTooLong')
        : z.string().max(100, 'errors.visitSummaryTooLong').optional().or(z.literal('')),
      visitTypeId: config.checkInType.required
        ? z.string().min(1, 'errors.checkInOptionsRequired')
        : z.string().optional().or(z.literal('')),
      checkInInstructions: config.checkInInstructions.required
        ? z.string().min(1, 'errors.checkInInstructionsRequired')
        : z.string().optional().or(z.literal('')),
      noteForReception: config.noteForReception.required
        ? z.string().min(1, 'errors.noteForReceptionRequired')
        : z.string().optional().or(z.literal('')),
      visitEntryOption: config.passEntryOptionEnabled
        ? z.string().min(1, 'errors.visitEntryOptionRequired')
        : z.string().optional().or(z.literal('')),
      acknowledgmentConfirmed: config.acknowledgmentText
        ? z.boolean().refine((val) => val === true, 'errors.acknowledgmentRequired')
        : z.boolean().optional(),
      email_opt_in: z.boolean().optional(),
      sms_opt_in: z.boolean().optional(),
      visitors: z.array(visitorSchema),
    })
    .refine(
      (data) => {
        // In multiple mode, require at least one visitor (from CSV)
        if (data.visitorMode === VisitorMode.MULTIPLE) {
          return data.visitors.length >= 1;
        }
        return true;
      },
      {
        message: 'errors.visitorCSVFileRequired',
        path: ['visitors'],
      },
    )
    .refine(
      (data) => {
        if (!config.notificationSelection.required) {
          return true;
        }
        return data.email_opt_in === true || data.sms_opt_in === true;
      },
      {
        message: 'errors.notificationSelectionRequired',
        path: ['sms_opt_in'],
      },
    );
};
